import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';


// Mismo endpoint que la app móvil (clienteApp); `app` separa los datos en el dashboard de lemu.
const APP_ORIGEN = 'portal';
const DEVICE_ID_KEY = 'metricas_device_id';
const QUERY_PARAMS_CAMPANA = ['utm_source', 'utm_medium', 'utm_campaign', 'gclid'];
// Lo que el backend acepta sin sesión (TIPOS_SIN_SESION en metricas_serializer.py); el resto responde 400
const TIPOS_SIN_SESION = ['app_open', 'screen_view', 'modal_open', 'login', 'error'];


@Injectable({
  providedIn: 'root',
})
export class MetricasService {

  private readonly endpoint = `${environment.API_ENDPOINT}mobile-cliente/metrics/`;
  private deviceIdMemoria: string | null = null;
  private ultimaPantalla = '';
  private iniciado = false;

  constructor(private http: HttpClient, private router: Router) { }

  /**
   * Arranca la medición automática: pantallas (cambios de ruta) y diálogos (MatDialog).
   * Se llama una sola vez desde AppComponent, así una pantalla o diálogo nuevo queda
   * medido sin escribir código de métricas.
   */
  init() {
    if (this.iniciado) {
      return;
    }
    this.iniciado = true;

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.registrarPantalla(e.urlAfterRedirects));

    this.interceptarDialogos();

    this.guardarMetrica({ tipo_metrica: 'app_open', data: { cause: 'inicio' } });
  }

  async guardarMetrica(payload: any) {
    // Corriendo contra el backend local (`--configuration local`) no se mide: esas métricas
    // caerían en la BD de dev y ensuciarían el dashboard con pruebas.
    if (environment.API_ENDPOINT.startsWith('http://localhost')) return;

    try {
      const token = localStorage.getItem('token');
      if (!token && !TIPOS_SIN_SESION.includes(payload?.tipo_metrica)) {
        return;
      }

      const data: any = { ...(payload?.data || {}) };

      data.platform = 'web';
      data.device = this.esMovil() ? 'mobile' : 'desktop';
      data.app = APP_ORIGEN;
      data.device_id = this.deviceId();
      data.date = data.date || new Date();

      // se lee al enviar: la empresa cambia al loguearse o al cambiar de empresa
      const companyId = Number(localStorage.getItem('company_id'));
      if (data.company_id == undefined && companyId) {
        data.company_id = companyId;
      }

      const options: any = token ? { headers: new HttpHeaders().set('Authorization', 'JWT ' + token) } : {};

      await this.http.post(this.endpoint, { tipo_metrica: payload.tipo_metrica, data }, options).toPromise();
    } catch (error: any) {
      console.error('Error al guardar metrica');
    }
  }

  private registrarPantalla(url: string) {
    let snapshot: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }

    const routePattern = snapshot.pathFromRoot
      .map((s) => s.routeConfig?.path)
      .filter((p) => !!p)
      .join('/');

    const pantalla = this.selectorDe(snapshot.routeConfig?.component) || routePattern;
    const ruta = url.split('?')[0].split('#')[0];

    // la misma pantalla con la misma ruta no se cuenta dos veces seguidas
    const clave = `${pantalla}|${ruta}`;
    if (!pantalla || clave == this.ultimaPantalla) {
      return;
    }
    this.ultimaPantalla = clave;

    const data: any = {
      screen: pantalla,
      route: ruta,
      route_pattern: routePattern,
    };

    for (const param of QUERY_PARAMS_CAMPANA) {
      const valor = snapshot.queryParamMap.get(param);
      if (valor) {
        data[param] = valor;
      }
    }

    this.guardarMetrica({ tipo_metrica: 'screen_view', data });
  }

  private interceptarDialogos() {
    const metricas = this;
    const openOriginal = MatDialog.prototype.open;

    MatDialog.prototype.open = function (this: MatDialog, componentOrTemplateRef: any, config?: any) {
      metricas.registrarDialogo(componentOrTemplateRef);
      return openOriginal.call(this, componentOrTemplateRef, config);
    } as any;
  }

  private registrarDialogo(component: any) {
    const pantalla = this.selectorDe(component);
    if (!pantalla) {
      return;
    }

    this.guardarMetrica({
      tipo_metrica: 'modal_open',
      data: {
        screen: pantalla,
        kind: 'dialog',
        route: this.router.url.split('?')[0],
      }
    });
  }

  // El selector sobrevive a la minificación de producción; el nombre de la clase no.
  private selectorDe(component: any): string | null {
    return component?.ɵcmp?.selectors?.[0]?.[0] || null;
  }

  private esMovil(): boolean {
    return typeof window !== 'undefined' && window.matchMedia?.('(max-width: 768px)').matches;
  }

  // localStorage.clear() se usa al cerrar sesión, así que el id también vive en memoria
  private deviceId(): string {
    let id = localStorage.getItem(DEVICE_ID_KEY) || this.deviceIdMemoria;
    if (!id) {
      id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    this.deviceIdMemoria = id;
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  }
}
